import {
  FC,
  PropsWithChildren,
  useMemo,
  useRef,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import { Form } from 'antd';

import {
  GenericDataFormContext,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../../shared/contexts';
import { StepTwoFormContext } from './StepTwoFormContext';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import {
  DECISION_TYPE,
  Feature,
  FeaturesVariousRisksRequest,
  Products,
  SelectOption,
} from '../../../../../../../../../shared/interfaces';
import { TabsPropertyDataHome } from '../../components/TabsPropertyDataHome';
import { HeaderTabHome } from '../../components/TabsPropertyDataHome/components';
import {
  LOADING_SAVED_QUOTATIONS_STATES,
  LoadSavedQuotations,
  PLAN_TYPE,
  PlanType,
} from '../../../../../shared';
import { displayErrorMessage } from '../../../../../../../../../shared/utils';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';

const planTypeEquivalent: { A: boolean; C: boolean } = {
  A: true,
  C: false,
};

const normalizeServiceAddress = (address?: string | null): string => {
  if (address === null || address === undefined) return '';

  const trimmed = String(address).trim();
  return trimmed || '';
};

export interface HousingTypeOptions {
  [key: string | number]: SelectOption[];
}

export const StepTwoFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    quotationInfo,
    savedQuotation,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    renewalQuotationState,
    setRenewalQuotationState,
    quotationToRenewal,
  } = useContext(QuotationInfoContext);
  const {
    setCanShowSpinner,
    getLocalitiesPerProvince,
    plansSelected,
    updateFormsAfterRemoveTabs,
    setRemovedTabIndexes,
    removedTabIndexes,
    setUserCanDeleteTabs
  } = useContext(GenericDataFormContext);

  const INITIAL_KEY_TAB = 1;
  const [activeKey, setActiveKey] = useState<number>(INITIAL_KEY_TAB);
  const [homeFormValues, setHomeFormValues] = useState<any>();

  const [plansByActivityList, setPlansByActivityList] = useState<
    Array<Products>
  >([]);
  const [planOptionsBySelectedPlan, setPlanOptionsBySelectedPlan] = useState<
    Array<SelectOption>
  >([]);
  const [planTypeSelected, setPlanTypeSelected] = useState<PlanType>(
    PLAN_TYPE.OPEN
  );

  const [isPlanTypeADisabled, setIsPlanTypeADisabled] = useState(false);
  const [isPlanTypeCDisabled, setIsPlanTypeCDisabled] = useState(false);

  const [areTabActionsAllowed, setAreTabActionsAllowed] =
    useState<boolean>(true);

  const [allFeatures, setAllFeatures] = useState<
    Array<{ plan: string; keyTab: number; features: Array<Feature> }>
  >([]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [keyTabToDelete, setKeyTabToDelete] = useState<number | null>(null);
  const { clientSelected } = useContext(StepOneFormContext);
  const [tabs, setTabs] = useState<Array<any>>([]);

  const [housingTypeOptionsPerPlan, setHousingTypeOptionsPerPlan] = useState<
    Array<{ plan: string; housingTypes: Array<SelectOption> }>
  >([]);

  const [stepTwoForm] = Form.useForm<any>();

  const newTabIndex = useRef(INITIAL_KEY_TAB);

  const addNewTab = useCallback(() => {
    if (tabs.length <= 5) {
      const newActiveKey = ++newTabIndex.current;
      const isTabActionEnabled =
        planTypeEquivalent[planTypeSelected ?? PLAN_TYPE.CLOSED];

      setTabs([
        ...tabs,
        {
          label: (
            <HeaderTabHome
              id={newActiveKey}
              areTabActionsAllowed={isTabActionEnabled}
            />
          ),
          children: <TabsPropertyDataHome id={newActiveKey} />,
          key: newActiveKey,
          closable: false,
          forceRender: true,
        },
      ]);

      if(removedTabIndexes.includes(newActiveKey)) {
        setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
      };

      setActiveKey(newActiveKey);

      return;
    }
    displayErrorMessage('Solo puede agregar hasta 6 Hogares');
  }, [
    tabs,
    newTabIndex, 
    setTabs, 
    setActiveKey, 
    planTypeSelected,
    setRemovedTabIndexes,
    removedTabIndexes
  ]);

  const duplicateTab = useCallback(
    (id: any) => {      
      if (tabs.length <= 5) {
        const tabSelected = tabs.find((tab: any) => tab.key === id);
        const newActiveKey = ++newTabIndex.current;
        const isTabActionEnabled =
          planTypeEquivalent[planTypeSelected ?? PLAN_TYPE.CLOSED];

        if (tabSelected) {
          const newTab = {
            ...tabSelected,
            key: newActiveKey,
            label: (
              <HeaderTabHome
                id={newActiveKey}
                areTabActionsAllowed={isTabActionEnabled}
              />
            ),
            children: (
              <TabsPropertyDataHome id={newActiveKey} duplicateId={id} />
            ),
            closable: false,
            forceRender: true,
          };
          setTabs([...tabs, newTab]);
          setActiveKey(newActiveKey);

          if(removedTabIndexes.includes(newActiveKey)) {
            setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
          };
        }
        return;
      }
      displayErrorMessage('Solo puede agregar hasta 6 Hogares');
    },
    [
      tabs, 
      newTabIndex, 
      setTabs, 
      setActiveKey, 
      planTypeSelected,
      removedTabIndexes,
      setRemovedTabIndexes,
    ]
  );

  const removeTab = useCallback(
    (targetKey: number) => {
      setCanShowSpinner(true);
      setTimeout(() => {
        updateFormsAfterRemoveTabs([targetKey], [stepTwoForm]);

        setRemovedTabIndexes((prevValues) => [...prevValues, targetKey]);
        const targetIndex = tabs.findIndex(
          (pane: any) => pane.key === targetKey
        );
        const newPanes = tabs
          .filter((pane: any) => pane.key !== targetKey)
          .map((tab, index) => ({
            ...tab,
            key: index + 1,
            label: (
              <HeaderTabHome
                id={index + 1}
                areTabActionsAllowed={areTabActionsAllowed}
              />
            ),
            children: <TabsPropertyDataHome id={index + 1} />,
          }));
        if (newPanes.length && targetKey === activeKey) {
          const { key } =
            newPanes[
              targetIndex === newPanes.length ? targetIndex - 1 : targetIndex
            ];
          setActiveKey(key);
        }
        newTabIndex.current = tabs.length - 1;
        setTabs(newPanes);
        setCanShowSpinner(false);
      }, 500);
    },
    [
      tabs, 
      setTabs, 
      setActiveKey, 
      activeKey,
      setCanShowSpinner,
      updateFormsAfterRemoveTabs,
      setRemovedTabIndexes,
      areTabActionsAllowed,
      stepTwoForm,
    ]
  );

  const resetTabs = useCallback(
    (unconfirmedPlanType: PLAN_TYPE.OPEN | PLAN_TYPE.CLOSED) => {
      const isTabActionEnabled = planTypeEquivalent[unconfirmedPlanType];
      setTabs([
        {
          label: (
            <HeaderTabHome
              id={INITIAL_KEY_TAB}
              areTabActionsAllowed={isTabActionEnabled}
            />
          ),
          children: <TabsPropertyDataHome id={INITIAL_KEY_TAB} />,
          key: INITIAL_KEY_TAB,
          closable: false,
          forceRender: true,
        },
      ]);

      stepTwoForm.resetFields();
      newTabIndex.current = INITIAL_KEY_TAB;
      setActiveKey(INITIAL_KEY_TAB);
    },
    [setTabs, setActiveKey, stepTwoForm]
  );

  const getListPlansActivity = useCallback(
    async (article: string, branch: string) => {
      if (plansByActivityList.length) return;

      setCanShowSpinner(true);
      try {
        const response = await QuotationService.getProductsListPerProducer(
          article,
          branch
        );

        if (response?.data?.productosPorProductor?.length) {
          const productosPorProductor = response?.data?.productosPorProductor;

          setPlansByActivityList(productosPorProductor);

          const hasPlanTypeA = productosPorProductor.some(
            (plan) => plan.tipoDePlan === PLAN_TYPE.OPEN
          );

          setPlanTypeSelected(hasPlanTypeA ? PLAN_TYPE.OPEN : PLAN_TYPE.CLOSED);
        }
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      setCanShowSpinner,
      setPlansByActivityList,
      setPlanTypeSelected,
      plansByActivityList,
    ]
  );

  const filterPlansByPlanTypeSelected = useCallback(() => {
    if (planTypeSelected) {
      const selectedPlanTypeValue: PLAN_TYPE.OPEN | PLAN_TYPE.CLOSED =
        planTypeSelected;

      if (!homeFormValues) {
        setTabs([
          {
            label: (
              <HeaderTabHome
                id={INITIAL_KEY_TAB}
                areTabActionsAllowed={planTypeEquivalent[selectedPlanTypeValue]}
              />
            ),
            children: <TabsPropertyDataHome id={INITIAL_KEY_TAB} />,
            key: INITIAL_KEY_TAB,
            closable: false,
            forceRender: true,
          },
        ]);
      }

      setAreTabActionsAllowed(planTypeEquivalent[selectedPlanTypeValue]);

      const filteredPlans = plansByActivityList.filter(
        (plan) => plan.tipoDePlan === selectedPlanTypeValue
      );

      const plansOptions = filteredPlans.map((el) => ({
        label: `${el.codigo} - ${el.descripcionLarga}`,
        value: el.codigo,
      }));

      setPlanOptionsBySelectedPlan(plansOptions);
    }
  }, [
    plansByActivityList,
    planTypeSelected,
    homeFormValues,
    setTabs,
    setAreTabActionsAllowed,
    setPlanOptionsBySelectedPlan,
  ]);

  const getFeaturesRisks = useCallback(
    async (
      planSelected: string,
      componente: number
    ): Promise<Array<Feature> | any> => {
      const params: FeaturesVariousRisksRequest = {
        numeroCotizacion: quotationInfo?.token!,
        rama: quotationInfo?.branch!,
        articuloRamaSecuencia: quotationInfo?.arse!,
        plan: planSelected,
        codigoPostal: clientSelected?.codigoPostal!,
        sufijoPostal: clientSelected?.sufijoCodigoPostal!,
        componente,
        ubicacionDeRiesgo: '',
      };
      setCanShowSpinner(true);
      try {
        const response = await QuotationService.postFeaturesVariousRisks(
          params
        );

        return response?.data?.caracteristicas ?? [];
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [quotationInfo, clientSelected, setCanShowSpinner]
  );

  const getFeaturesRiskByPlan = useCallback(
    async (id: number, planSelected: string, componente: number) => {
      const isFeatureAlreadyAdded = allFeatures.some(
        (feature) => String(feature.plan) === String(planSelected)
      );
      
      if (isFeatureAlreadyAdded) return;

      setCanShowSpinner(true);

      try {
        const featuresList = await getFeaturesRisks(planSelected, componente);
        
        setAllFeatures((prevValues) => [
          ...prevValues,
          { plan: planSelected, keyTab: id, features: featuresList },
        ]);
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [setCanShowSpinner, setAllFeatures, getFeaturesRisks, allFeatures]
  );

  const getTypeHousingByPlan = useCallback(
    async (plan: string) => {
      setCanShowSpinner(true);

      try {
        const response = await QuotationService.getTypesHousing(
          quotationInfo?.branch!,
          plan
        );
        if (response.data) {
          return response.data;
        }
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [setCanShowSpinner, quotationInfo]
  );

  const getOptionsTypesHousing = useCallback(
    async (planSelected: string) => {
      if (
        housingTypeOptionsPerPlan.some(
          (el) => String(el.plan) === String(planSelected)
        )
      )
        return;

      try {
        setCanShowSpinner(true);

        const response = await getTypeHousingByPlan(planSelected);

        const options: Array<SelectOption> =
          response?.tiposVivienda?.map(({ codigo, descripcion }) => ({
            label: descripcion,
            value: codigo,
          })) ?? [];

        setHousingTypeOptionsPerPlan((prevValues) => [
          ...prevValues,
          { plan: planSelected, housingTypes: options },
        ]);
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      setCanShowSpinner,
      housingTypeOptionsPerPlan,
      getTypeHousingByPlan,
      setHousingTypeOptionsPerPlan,
    ]
  );

  const fillStepTwoServices = useCallback(async (isRenewal: boolean) => {
    try {      
      const provincesSelected: Array<string> = [];
      const plansSelected: Array<{
        componentId: string | number;
        plan: string;
      }> = [];

      let quotationToProccess;
      if(isRenewal){
        quotationToProccess = quotationToRenewal;
      }else{
        quotationToProccess = savedQuotation;
      }
      
      const promises: Array<Promise<void>> = [
        getListPlansActivity(quotationInfo?.article!, quotationInfo?.branch!),
      ];

      quotationToProccess.bienes.forEach((bien: any) => {
        if (!provincesSelected.includes(String(bien.provincia))) {
          provincesSelected.push(String(bien.provincia));
        }

        if (
          !plansSelected.some(
            (el) =>
              el.componentId === String(bien.numeroComponente) &&
              el.plan === String(bien.planSeleccionado)
          )
        ) {
          plansSelected.push({
            componentId: String(bien.numeroComponente),
            plan: String(bien.planSeleccionado),
          });
        }
      });

      provincesSelected.forEach((province) =>
        promises.push(getLocalitiesPerProvince(province))
      );

      plansSelected.forEach(({ componentId, plan }) => {
        promises.push(
          getFeaturesRiskByPlan(
            Number(componentId),
            plan,
            Number(componentId)
          )
        );
        promises.push(getOptionsTypesHousing(plan));
      });

      await Promise.all(promises).then(() => {
          if (isRenewal) {
            setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL)
          }else{
            setLoadSaveQuotationState(
              LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED
            )
          }
      });
    } catch (error) {}
  }, [
    getListPlansActivity,
    savedQuotation,
    quotationToRenewal,
    getFeaturesRiskByPlan,
    getLocalitiesPerProvince,
    quotationInfo,
    setLoadSaveQuotationState,
    getOptionsTypesHousing,
  ]);

  const fillStepTwoData = useCallback(async (isRenewal: boolean) => {
    
    let quotationToProccess;
    if(isRenewal){
      quotationToProccess = quotationToRenewal;
    }else{
      quotationToProccess = savedQuotation;
    }    
    const { bienes = [], tipoDePlan = PLAN_TYPE.OPEN } = quotationToProccess as LoadSavedQuotations;

    const isTabActionEnabled = planTypeEquivalent[tipoDePlan];

    const _tabs: Array<any> = [];

    const _inputs: Array<{ name: string; value: string | boolean }> = [];

    bienes.forEach(
      (
        {
          planSeleccionado,
          provincia,
          sufijoPostal,
          codigoPostal,
          tipoDeVivienda,
          domicilio,
          caracteristicasSeleccionadas = [],
        },
        index: number
      ) => {
        const _activeKey = index + 1;

        _tabs.push({
          label: (
            <HeaderTabHome
              id={_activeKey}
              areTabActionsAllowed={isTabActionEnabled}
            />
          ),
          children: <TabsPropertyDataHome id={_activeKey} />,
          key: _activeKey,
          closable: false,
          forceRender: true,
        });

        newTabIndex.current = _activeKey;

        _inputs.push({
          name: `plan-tab-${_activeKey}`,
          value: `${planSeleccionado}`,
        });

        _inputs.push({
          name: `province-tab-${_activeKey}`,
          value: `${provincia}`,
        });

        _inputs.push({
          name: `location-tab-${_activeKey}`,
          value: `${sufijoPostal}${codigoPostal}`,
        });

        _inputs.push({
          name: `type-housing-tab-${_activeKey}`,
          value: `${tipoDeVivienda}`,
        });

        _inputs.push({
          name: `propertyDataAddress${_activeKey}`,
          value: normalizeServiceAddress(domicilio),
        });

        caracteristicasSeleccionadas.forEach((caracteristica: any) => {
          _inputs.push({
            name: `feature-tab-${_activeKey}-${caracteristica.codigo}`,
            value: caracteristica.tiene === DECISION_TYPE.YES,
          });
        });
      }
    );

    setUserCanDeleteTabs(false);

    setTabs(_tabs);

    setPlanTypeSelected(quotationToProccess.tipoDePlan);

    setActiveKey(INITIAL_KEY_TAB);

    const formValues = _inputs.reduce((acc, { name, value }) => {
      acc[name] = value;
      return acc;
    }, {} as Record<string, any>);

    stepTwoForm.setFields(_inputs);
    stepTwoForm.setFieldsValue(formValues);

    setHomeFormValues(formValues);
  }, [
    planTypeSelected,
    savedQuotation,
    quotationToRenewal,
    setActiveKey,
    setTabs,
    stepTwoForm,
    setHomeFormValues,
    setPlanTypeSelected,
    newTabIndex,
    setUserCanDeleteTabs,
  ]);

  useEffect(() => {
    if (!homeFormValues && quotationInfo && !loadSaveQuotationState) {
      getListPlansActivity(quotationInfo.article, quotationInfo.branch);
    }
  }, [homeFormValues, quotationInfo, loadSaveQuotationState]);

  useEffect(() => {
    if (!homeFormValues && quotationInfo && !renewalQuotationState) {
      getListPlansActivity(quotationInfo.article, quotationInfo.branch);
    }
  }, [homeFormValues, quotationInfo, renewalQuotationState]);

  useEffect(() => {
    if (savedQuotation) {
      setIsPlanTypeADisabled(true);
      setIsPlanTypeCDisabled(true);
    } else if (plansByActivityList.length) {
      const hasPlanTypeA = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.OPEN
      );

      const hasPlanTypeC = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.CLOSED
      );

      setIsPlanTypeADisabled(!hasPlanTypeA);
      setIsPlanTypeCDisabled(!hasPlanTypeC);
    }
  }, [plansByActivityList, savedQuotation]);

  useEffect(() => {
    if (plansByActivityList.length && planTypeSelected) {
      filterPlansByPlanTypeSelected();
    }
  }, [planTypeSelected, plansByActivityList]);

  useEffect(() => {        
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED &&
      plansSelected.length
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING
      );
      fillStepTwoServices(false);
    }
    /* eslint-disable-next-line */
  }, [loadSaveQuotationState, plansSelected]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED
    ) {      
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING
      );
      fillStepTwoData(false);
    }
    /* eslint-disable-next-line */
  }, [loadSaveQuotationState]);

  useEffect(() => {    
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING &&
      tabs.length === savedQuotation.bienes.length &&
      homeFormValues
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED
      );
    }
    /* eslint-disable-next-line */
  }, [loadSaveQuotationState, savedQuotation, tabs, homeFormValues]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLED_RENEWAL &&
      plansSelected.length
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADING_RENEWAL
      );
      fillStepTwoServices(true);
    }
    /* eslint-disable-next-line */
  }, [renewalQuotationState, plansSelected]);

  useEffect(() => {    
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL
      );
      fillStepTwoData(true);
    }
    /* eslint-disable-next-line */
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL &&
      tabs.length === quotationToRenewal.bienes.length &&
      homeFormValues
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL
      );
    }
    /* eslint-disable-next-line */
  }, [renewalQuotationState, quotationToRenewal, tabs, homeFormValues]);

  const values = useMemo(
    () => ({
      activeKey,
      addNewTab,
      allFeatures,
      areTabActionsAllowed,
      duplicateTab,
      filterPlansByPlanTypeSelected,
      getFeaturesRisks,
      homeFormValues,
      isPlanTypeADisabled,
      isPlanTypeCDisabled,
      keyTabToDelete,
      newTabIndex,
      planOptionsBySelectedPlan,
      planTypeSelected,
      removeTab,
      resetTabs,
      setActiveKey,
      setAllFeatures,
      setHomeFormValues,
      setKeyTabToDelete,
      setPlanTypeSelected,
      setShowModal,
      setTabs,
      showModal,
      stepTwoForm,
      tabs,
      getOptionsTypesHousing,
      housingTypeOptionsPerPlan,
      setIsPlanTypeADisabled,
      setIsPlanTypeCDisabled,
    }),
    [
      activeKey,
      addNewTab,
      allFeatures,
      areTabActionsAllowed,
      duplicateTab,
      filterPlansByPlanTypeSelected,
      getFeaturesRisks,
      homeFormValues,
      isPlanTypeADisabled,
      isPlanTypeCDisabled,
      keyTabToDelete,
      newTabIndex,
      planOptionsBySelectedPlan,
      planTypeSelected,
      removeTab,
      resetTabs,
      setActiveKey,
      setAllFeatures,
      setHomeFormValues,
      setKeyTabToDelete,
      setPlanTypeSelected,
      setShowModal,
      setTabs,
      showModal,
      stepTwoForm,
      tabs,
      getOptionsTypesHousing,
      housingTypeOptionsPerPlan,
      setIsPlanTypeADisabled,
      setIsPlanTypeCDisabled,
    ]
  );

  return (
    <StepTwoFormContext.Provider value={values}>
      {children}
    </StepTwoFormContext.Provider>
  );
};
